import { useMemo } from "react";

import { isPostcodeInAllowedArea } from "@thalamos/vision-common";

import { useUser } from "../../../Context/UserContext.js";

/**
 * Hook to determine if a Place of Safety is outside the force's configured area.
 *
 * The warning is shown when:
 * 1. Force area postcode prefixes are configured (non-empty array = feature enabled)
 * 2. The selected POS postcode prefix is NOT in the allowed list
 *
 * @param postalCode - The postal code of the selected Place of Safety
 * @returns Object with `showWarning` boolean and `isEnabled` flag
 */
export function useOutsideForceAreaWarning(
  postalCode: string | undefined | null,
): {
  showWarning: boolean;
  isEnabled: boolean;
} {
  const { user } = useUser();

  const forceAreaPrefixes =
    user?.sessionOrganisationConfiguration?.forceAreaPostcodePrefixes ?? [];

  // Feature is enabled if prefixes are configured
  const isEnabled = forceAreaPrefixes.length > 0;

  const showWarning = useMemo(() => {
    if (!isEnabled || !postalCode) {
      return false;
    }

    return !isPostcodeInAllowedArea(postalCode, forceAreaPrefixes);
  }, [isEnabled, forceAreaPrefixes, postalCode]);

  return { showWarning, isEnabled };
}
